import { createStaffCatalogApi } from "@lafayette/api-client";

import { createAdminSupabaseClient } from "../auth/supabase";
import { getAdminPublicConfig } from "../config/public-config";
import { type CatalogRuntime, putFileToTarget } from "./runtime";

/**
 * The development placeholder's token. The module that builds it is imported only behind
 * `import.meta.env.DEV`, which a production build replaces with `false`: the branch is removed, and the
 * module, with its token prefix, is not in the production bundle or its source map.
 */
async function developmentToken(): Promise<string | null> {
  if (!import.meta.env.DEV) return null;
  const { developmentAccessToken } = await import("../auth/development-access-token");
  return developmentAccessToken();
}

/** The bearer token of the current session: the dev placeholder's token, or the Supabase access token. */
export async function currentAccessToken(): Promise<string | null> {
  const config = getAdminPublicConfig();
  if (config.developmentAuthPlaceholderEnabled) return developmentToken();
  const client = createAdminSupabaseClient();
  if (client === null) return null;
  const { data } = await client.auth.getSession();
  return data.session?.access_token ?? null;
}

/** The runtime of the real admin app: the typed client over `fetch`, a direct upload to storage, the clock. */
export function createAdminCatalogRuntime(): CatalogRuntime {
  const config = getAdminPublicConfig();
  return {
    api: createStaffCatalogApi({
      baseUrl: config.apiUrl.toString(),
      getAccessToken: currentAccessToken,
    }),
    uploadFile: (target, file) => putFileToTarget(target, file),
    now: () => new Date(),
  };
}
